import {
    getObjects,
    createObjects,
    updateObjects,
    updateShadow,
    getShadow,
} from './../api/webApplicationApi';
import { AirCurtainGroup } from './airCurtainGroup';

const objectType = 'airCurtain';

export class AirCurtain {
    private _saveMethod = createObjects;
    private _object: omAirCurtain;
    private _shadow: AirCurtainShadow2;
    private _lastError: Error;

    public constructor(airCurtainID: string) {
        this._object = {
            name: undefined,
            airCurtainID: airCurtainID,
            airCurtainGroups: [],
            connected: undefined,
            connectivityTimestamp: undefined,
            eccPub: undefined,
            heaterPower: undefined,
            model: undefined,
            pairingKey: undefined,
        };
    }

    public static createNew(airCurtainID: string): AirCurtain {
        return new AirCurtain(airCurtainID);
    }

    public static createFromObject(
        airCurtain: omAirCurtain,
        shadow: AirCurtainShadow2
        ): AirCurtain {
        const obj = new AirCurtain(airCurtain.airCurtainID);
        obj._object = airCurtain;
        obj._shadow = shadow;
        obj._saveMethod = updateObjects;
        return obj;
    }

    public async load(): Promise<boolean> {
        const getObject: GetRequest = {
            objectType: objectType,
            keys: [this._object.airCurtainID],
        };
        const getShadowRequest: GetShadowRequest = {
            airCurtainID: this._object.airCurtainID,
        };
        this._lastError = undefined;
        try {
            const response = await getObjects([getObject]);
            if (
                response &&
                response.length > 0 &&
                response[0].objects &&
                response[0].objects.length == 1
            ) {
                this._saveMethod = updateObjects;
                this._object = response[0].objects[0];
                this._shadow = await getShadow(getShadowRequest);
                return true;
            } else {
                this._lastError = new Error('air curtain not found');
                return false;
            }
        } catch (error) {
            this._lastError = error as Error;
            return false;
        }
    }

    public async refreshShadow(): Promise<boolean> {
        const getShadowRequest: GetShadowRequest = {
            airCurtainID: this._object.airCurtainID,
        };
        this._lastError = undefined;
        try {
            this._shadow = await getShadow(getShadowRequest);
            return true;
        } catch (error: any) {
            this._lastError = error;
            return false;
        }
    }

    public async saveProperties(): Promise<boolean> {
        const saveObject: CreateUpdateDeleteRequest = {
            objectType: objectType,
            object: this._object,
        };
        this._lastError = undefined;

        if (this._object.name == undefined) {
            this._lastError = new Error('Name is undefined');
            return false;
        }
        if (this._object.heaterPower == undefined) {
            this._lastError = new Error('Heater Power is undefined');
            return false;
        }
        if (this._object.model == undefined) {
            this._lastError = new Error('Model is undefined');
            return false;
        }
        try {
            const response = await this._saveMethod([saveObject]);
            if (response) {
                this._saveMethod = updateObjects;
            } else {
                this._lastError = new Error('unknown server error');
            }
            return response;
        } catch (error: any) {
            this._lastError = error;
            return false;
        }
    }

    private async saveAirCurtainDesired(
        desired: AirCurtainShadow2
    ): Promise<boolean> {
        const updateShadowRequest: UpdateShadowRequest = {
            airCurtainID: this._object.airCurtainID,
            desired: desired,
        };
        this._lastError = undefined;
        try {
            const response = await updateShadow(updateShadowRequest);
            if (!response) {
                this._lastError = new Error('unknown server error');
            }
            return response;
        } catch (error: any) {
            this._lastError = error;
            return false;
        }
    }

    // public async delete() {
    //     const deleteObject:CreateUpdateDeleteRequest = {
    //         objectType: objectType,
    //         object: this._object
    //     };
    //     return new Promise(resolve => deleteObjects([deleteObject]).then(response => resolve(response)));
    // }

    //object properties
    public get lastError(): Error {
        return this._lastError;
    }

    public get airCurtainID(): string {
        return this._object.airCurtainID;
    }

    public get name(): string {
        return this._object.name;
    }

    public set name(name: string) {
        this._object.name = name;
    }

    public get airCurtainGroups(): Array<string> {
        if (this._object.airCurtainGroups === undefined) {
            this._object.airCurtainGroups = [];
        }

        return this._object.airCurtainGroups;
    }

    public addAirCurtainGroup(airCurtainGroup: AirCurtainGroup): void {
        if (this._object.airCurtainGroups.includes(airCurtainGroup.airCurtainGroupID)) return;
        this._object.airCurtainGroups.push(airCurtainGroup.airCurtainGroupID);
    }

    public deleteAirCurtainGroup(airCurtainGroup: AirCurtainGroup): void {
        const index = this._object.airCurtainGroups.indexOf(airCurtainGroup.airCurtainGroupID);
        if (index > -1) { 
            this._object.airCurtainGroups.splice(index, 1);
        }
    }

    public get connected(): boolean {
        return this._object.connected;
    }

    public get connectivityTimestamp(): number {
        return this._object.connectivityTimestamp;
    }

    public get eccPub(): string {
        return this._object.eccPub;
    }

    public get heaterPower(): number {
        return this._object.heaterPower;
    }

    public set heaterPower(heaterPower: number) {
        this._object.heaterPower = heaterPower;
    }

    public get model(): string {
        return this._object.model;
    }

    public set model(model: string) {
        this._object.model = model;
    }

    public get pairingKey(): string {
        return this._object.pairingKey;
    }

    public get provisionedTimestamp(): number {
        return this._object.provisionedTimestamp;
    }    

    // shadow properties
    public get isShadowLoaded(): boolean {
        return this._shadow != undefined;
    }

    public get bacnetConfiguration(): BacnetConfiguration {
        if (!this.isShadowLoaded) return undefined;

        return this._shadow.bacnetConfiguration
            ? {
                  enabled: this._shadow.bacnetConfiguration.enabled,
                  deviceName: this._shadow.bacnetConfiguration.deviceName,
                  deviceId: this._shadow.bacnetConfiguration.deviceId,
                  udpPort: this._shadow.bacnetConfiguration.udpPort,
                  bbmdEnabled: this._shadow.bacnetConfiguration.bbmdEnabled,
                  bbmdAddress: this._shadow.bacnetConfiguration.bbmdAddress,
                  bbmdPort: this._shadow.bacnetConfiguration.bbmdPort,
                  bbmdTtl: this._shadow.bacnetConfiguration.bbmdTtl,
                  interface: this._shadow.bacnetConfiguration.interface,
              }
            : undefined;
    }

    public async setBacnetConfiguration(
        bacnetConfiguration: BacnetConfiguration
    ): Promise<boolean> {
        if (!this.isShadowLoaded) return false;

        const bacnetConfigurationShadow: BacnetConfigurationShadow = {
            enabled:
                bacnetConfiguration.enabled != undefined
                    ? bacnetConfiguration.enabled
                    : this._shadow.bacnetConfiguration.enabled,
            deviceName:
                bacnetConfiguration.deviceName != undefined
                    ? bacnetConfiguration.deviceName
                    : this._shadow.bacnetConfiguration.deviceName,
            deviceId:
                bacnetConfiguration.deviceId != undefined
                    ? bacnetConfiguration.deviceId
                    : this._shadow.bacnetConfiguration.deviceId,
            udpPort:
                bacnetConfiguration.udpPort != undefined
                    ? bacnetConfiguration.udpPort
                    : this._shadow.bacnetConfiguration.udpPort,
            bbmdEnabled:
                bacnetConfiguration.bbmdEnabled != undefined
                    ? bacnetConfiguration.bbmdEnabled
                    : this._shadow.bacnetConfiguration.bbmdEnabled,
            bbmdAddress:
                bacnetConfiguration.bbmdAddress != undefined
                    ? bacnetConfiguration.bbmdAddress
                    : this._shadow.bacnetConfiguration.bbmdAddress,
            bbmdPort:
                bacnetConfiguration.bbmdPort != undefined
                    ? bacnetConfiguration.bbmdPort
                    : this._shadow.bacnetConfiguration.bbmdPort,
            bbmdTtl:
                bacnetConfiguration.bbmdTtl != undefined
                    ? bacnetConfiguration.bbmdTtl
                    : this._shadow.bacnetConfiguration.bbmdTtl,
            interface:
                bacnetConfiguration.interface != undefined
                    ? bacnetConfiguration.interface
                    : this._shadow.bacnetConfiguration.interface,
        };
        const desired: AirCurtainShadow2 = {
            bacnetConfiguration: bacnetConfigurationShadow,
        };
        const result = await this.saveAirCurtainDesired(desired);
        if (result) {
            this._shadow.bacnetConfiguration = bacnetConfigurationShadow;
        }
        return result;
    }

    public get cpuSerial(): string {
        if (!this.isShadowLoaded) return undefined;

        if (this._shadow.cpuSerial)
            return this._shadow.cpuSerial.replace(/^[0]+/g, '');
        else return 'N/A';
    }

    public get doorState(): doorState {
        if (!this.isShadowLoaded) return undefined;

        return this._shadow.doorState;
    }

    public get doorOpenCount(): number {
        if (!this.isShadowLoaded) return undefined;

        return this._shadow.doorOpenCount;
    }

    public get eth0Ip(): string {
        if (!this.isShadowLoaded) return undefined;

        return this._shadow.eth0Ip;
    }

    public get eth0Mac(): string {
        if (!this.isShadowLoaded) return undefined;

        return this._shadow.eth0Mac;
    }

    public get externalThermostatContactState(): number {
        if (!this.isShadowLoaded) return undefined;

        return this._shadow.externalThermostatContactState;
    }

    public get thermostatProbeModeState(): number {
        if (!this.isShadowLoaded) return undefined;

        return this._shadow.thermostatProbeModeState;
    }

    public get heaterRelayState(): number {
        if (!this.isShadowLoaded) return undefined;

        return this._shadow.heaterRelayState;
    }

    public get sensorTemperature(): number {
        if (!this.isShadowLoaded) return undefined;

        return this._shadow.temperature;
    }

    public get intelliswitchVersion(): number {
        if (!this.isShadowLoaded) return undefined;

        return this._shadow.intelliswitchVersion;
    }

    public get lineFrequency(): number {
        if (!this.isShadowLoaded) return undefined;

        return this._shadow.lineFrequency;
    }

    public get mode(): modeState {
        if (!this.isShadowLoaded) return undefined;

        return this._shadow.modeState;
    }

    public async setMode(mode: modeState): Promise<boolean> {
        if (!this.isShadowLoaded) return false;

        const desired: AirCurtainShadow2 = {
            modeState: mode,
        };
        const result = await this.saveAirCurtainDesired(desired);
        if (result) {
            this._shadow.modeState = mode;
        }
        return result;
    }

    public get motorRpm(): number {
        if (!this.isShadowLoaded) return undefined;

        return this._shadow.motorRpm;
    }

    public get osVersion(): string {
        if (!this.isShadowLoaded) return undefined;

        return this._shadow.osVersion;
    }

    public get wcsVersion(): string {
        if (!this.isShadowLoaded) return undefined;

        return this._shadow.wcsVersion;
    }

    public get pureMode(): pureMode {
        if (!this.isShadowLoaded) return undefined;

        return this._shadow.pureMode;
    }

    public get schedule(): Schedule {
        if (!this.isShadowLoaded) return undefined;

        return this._shadow.schedule
            ? {
                  enabled: this._shadow.schedule.enabled,
                  events: this._shadow.schedule.events
                      ? this._shadow.schedule.events.map((item) => {
                            return {
                                dayMinute: item.day_minute,
                                mode: item.mode,
                                weekDay: item.week_day,
                                enabled: item.enabled,
                            };
                        })
                      : [],
              }
            : undefined;
    }

    public async setSchedule(schedule: Schedule): Promise<boolean> {
        if (!this.isShadowLoaded) return false;

        const scheduleShadow: ScheduleShadow = {
            enabled: schedule.enabled,
            events: schedule.events.map((item) => {
                return {
                    day_minute: item.dayMinute,
                    mode: item.mode,
                    week_day: item.weekDay,
                    enabled: item.enabled,
                };
            }),
        };
        const desired: AirCurtainShadow2 = {
            schedule: scheduleShadow,
        };
        const result = await this.saveAirCurtainDesired(desired);
        if (result) {
            this._shadow.schedule = scheduleShadow;
        }
        return result;
    }

    public get fanSpeed(): number {
        if (!this.isShadowLoaded) return undefined;

        return this._shadow.fanSpeed;
    }

    public async setFanSpeed(fanSpeed: number): Promise<boolean> {
        if (!this.isShadowLoaded) return false;

        const desired: AirCurtainShadow2 = {
            fanSpeed: fanSpeed,
        };
        const result = await this.saveAirCurtainDesired(desired);
        if (result) {
            this._shadow.fanSpeed = fanSpeed;
        }
        return result;
    }

    // fan speeds
    public get fanSpeeds(): number {
        if (!this.isShadowLoaded) return undefined;

        switch (this._shadow.speedRange) {
            case 3:
            case 4:
            case 5:
                return 1;

            case 10:
            case 11:
                return 2;

            case 6:
            case 7:
            case 8:
            case 9:
                return 3;

            case 0:
            case 1:
            case 2:
            case 12:
            case 13:
            case 14:
            case 15:
            case 16:
            case 20:
            case 21:
            case 22:
            case 23:
            case 30:
            case 31:
            case 32:
            case 33:
            case 40:
            case 41:
            case 42:
            case 43:
                return 10;

            default:
                return 0;
        }
    }

    // has speed control
    get hasSpeedControl(): boolean {
        if (!this.isShadowLoaded) return undefined;

        return this.fanSpeeds > 0;
    }

    // has thermostat
    public get hasThermostat(): boolean {
        if (!this.isShadowLoaded) return undefined;

        switch (this._shadow.speedRange) {
            case 2:
            case 4:
            case 5:
            case 7:
            case 9:
            case 11:
            case 12:
            case 14:
            case 16:
            case 21:
            case 23:
            case 31:
            case 33:
            case 41:
            case 43:
                return true;

            default:
                return false;
        }
    }

    public get speedRange(): number {
        if (!this.isShadowLoaded) return undefined;

        return this._shadow.speedRange;
    }

    public get temperatureSetPoint(): number {
        if (!this.isShadowLoaded) return undefined;

        return this._shadow.temperatureSetPoint;
    }

    public async setTemperatureSetPoint(
        temperatureSetPoint: number
    ): Promise<boolean> {
        if (!this.isShadowLoaded) return false;

        const desired: AirCurtainShadow2 = {
            temperatureSetPoint: temperatureSetPoint,
        };
        const result = await this.saveAirCurtainDesired(desired);
        if (result) {
            this._shadow.temperatureSetPoint = temperatureSetPoint;
        }
        return result;
    }

    public get timeDelay(): number {
        if (!this.isShadowLoaded) return undefined;

        return this._shadow.timeDelay;
    }

    public async setTimeDelay(timeDelay: number): Promise<boolean> {
        if (!this.isShadowLoaded) return false;

        const desired: AirCurtainShadow2 = {
            timeDelay: timeDelay,
        };
        const result = await this.saveAirCurtainDesired(desired);
        if (result) {
            this._shadow.timeDelay = timeDelay;
        }
        return result;
    }

    public get maxTimestamp(): Date {
        if (!this.isShadowLoaded) return undefined;

        return this._shadow.maxTimestamp;
    }

    public get timezone(): string {
        if (!this.isShadowLoaded) return undefined;

        return this._shadow.timezone;
    }

    public async setTimezone(timezone: string): Promise<boolean> {
        if (!this.isShadowLoaded) return false;

        const desired: AirCurtainShadow2 = {
            timezone: timezone,
        };
        const result = await this.saveAirCurtainDesired(desired);
        if (result) {
            this._shadow.timezone = timezone;
        }
        return result;
    }

    public get version(): string {
        if (!this.isShadowLoaded) return undefined;

        return this._shadow.version;
    }

    public get wifiSSID(): string {
        if (!this.isShadowLoaded) return undefined;

        return this._shadow.wifiSSID;
    }

    public get wlan0Ip(): string {
        if (!this.isShadowLoaded) return undefined;

        return this._shadow.wlan0Ip;
    }

    public get wlan0Mac(): string {
        if (!this.isShadowLoaded) return undefined;

        return this._shadow.wlan0Mac;
    }
}
